import { config } from '../../config';
import DigiobcanApi, { type WebPushSubscription } from '../api/DigiobcanApi';
import { urlBase64ToUint8Array } from '../utils';
import { useLogger } from './LoggerProvider';
import React, {
  createContext,
  type Context,
  type PropsWithChildren,
} from 'react';

export type WebPushContextType = {
  subscribe: (options?: {
    askNotifications?: boolean;
  }) => Promise<WebPushSubscription | undefined>;
  unsubscribe: () => Promise<void>;
  getSubscription: () => Promise<WebPushSubscription | undefined>;
};

export const WebPushContext: Context<WebPushContextType> =
  createContext<WebPushContextType>({
    subscribe: async () => {
      return undefined;
    },
    unsubscribe: async () => {},
    getSubscription: async () => {
      return undefined;
    },
  });

const WebPushProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { logger } = useLogger();
  const [registration, setRegistration] =
    React.useState<ServiceWorkerRegistration>();

  const setupServiceWorker = React.useCallback(async () => {
    try {
      setRegistration(await navigator?.serviceWorker?.register('/sw.js'));
    } catch (error) {
      logger.error('Service Worker registration failed', {
        action: 'registerServiceWorker',
        error,
      });
    }
  }, [logger]);

  const subscribe = async (
    options: {
      existing?: PushSubscription;
      askNotifications?: boolean;
    } = {},
  ) => {
    if (
      !('serviceWorker' in navigator) ||
      !('PushManager' in window) ||
      !registration
    ) {
      return;
    }

    const permission = options.askNotifications
      ? await Notification.requestPermission()
      : Notification.permission;
    if (permission !== 'granted') {
      console.warn('Permission denied');
      return;
    }
    const newSubscription =
      options.existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.VAPID_PUBLIC_KEY),
      }));

    try {
      const { subscription } =
        await DigiobcanApi.meNotificationsWebPushSubscribe(
          {},
          {
            endpoint: newSubscription.endpoint,
            keys: {
              p256dh: newSubscription.getKey('p256dh')
                ? btoa(
                    String.fromCharCode(
                      ...new Uint8Array(newSubscription.getKey('p256dh')),
                    ),
                  )
                : '',
              auth: newSubscription.getKey('auth')
                ? btoa(
                    String.fromCharCode(
                      ...new Uint8Array(newSubscription.getKey('auth')),
                    ),
                  )
                : '',
            },
          },
        );
      return subscription;
    } catch (error) {
      logger.error('Failed to register web push', {
        action: 'registerWebPush',
        error,
      });
    }
  };

  const getSubscription = async () => {
    if (!registration) return;

    try {
      const sub = await registration.pushManager.getSubscription();

      if (sub) {
        return await subscribe({ existing: sub });
      }
    } catch (error) {
      logger.error('Failed to get subscription', {
        action: 'getWebPushSubscription',
        error,
      });
    }
  };

  const unsubscribe = async () => {
    if (!registration) return;
    const sub = await registration.pushManager.getSubscription();
    try {
      await DigiobcanApi.meNotificationsWebPushUnsubscribe({
        endpoint: sub.endpoint,
      });
      await sub.unsubscribe();
    } catch (error) {
      logger.error('Failed to unsubscribe from web push', {
        action: 'unsubscribeWebPush',
        error,
      });
    }
  };

  React.useEffect(() => {
    Promise.all([setupServiceWorker()]).then(() => {
      console.log('Service Worker and Push Manager setup complete');
    });
  }, [setupServiceWorker]);

  return (
    <WebPushContext.Provider
      value={{
        subscribe,
        unsubscribe,
        getSubscription,
      }}
    >
      {children}
    </WebPushContext.Provider>
  );
};

export const useWebPush = () => React.useContext(WebPushContext);

export default WebPushProvider;
